import React from "react";

import {
  DEFAULT_EGG_ID,
  DEFAULT_EGG_LIGHTS,
  DEFAULT_EGG_REDSHIFT,
  DEFAULT_EGG_TRANSFORM,
  DEFAULT_EGG_WARMTH,
  DEFAULT_MARC_TRANSFORM,
  DEFAULT_PALETTE,
  ONION_OPACITY,
} from "../constants";
import { writeUnlabeledHandoff } from "../handoff";
import { eggImageUrl, marcImageUrl } from "../paths";
import saveReference from "../saveReference";
import { MODES } from "../urlParams";
import useKey from "./useKey";
import useLocalStorageState from "./useLocalStorageState";

// One hook for everything author-mode-related: per-marc persisted state,
// transient editing UI, hotkeys, the save callback, and the small
// derived values App/DrawingArea/AuthorUIPanel all need (marc-onion src,
// egg warmth slider). Returns a flat bundle so consumers can destructure
// just the fields they touch.
//
// Three buckets of state in here:
//   1. Persisted per-marc data (palette, transforms, light)  — localStorage-backed
//   2. Transient editing UI (visibility, opacity, modes, A/B toggles, warmth)
//   3. Resets, derived values, and the save callback
//
// `authorMode` (derived from `mode`) seeds marcVisible — Marc onion is
// visible by default when authoring; hidden in play mode.
export default function useAuthoring({ urlState, mode, canvasRef }) {
  const authorMode = mode === MODES.AUTHOR;
  const key = (suffix) => `egg-game:v1:marc:${urlState.marcId}:${suffix}`;

  // ── Persisted per-marc state ──────────────────────────────────────
  const [palette, setPalette] = useLocalStorageState(
    key("palette"),
    DEFAULT_PALETTE,
  );
  const [marcTransform, setMarcTransform] = useLocalStorageState(
    key("marcTransform"),
    DEFAULT_MARC_TRANSFORM,
  );
  const [eggTransform, setEggTransform] = useLocalStorageState(
    key("eggTransform"),
    DEFAULT_EGG_TRANSFORM,
  );
  const [eggLights, setEggLights] = useLocalStorageState(
    key("eggLights"),
    DEFAULT_EGG_LIGHTS,
  );

  // Defensive palette reset: if DEFAULT_PALETTE's slot count changes
  // between versions, a stored palette from the old shape won't map.
  React.useEffect(() => {
    if (palette.length !== DEFAULT_PALETTE.length) {
      setPalette(DEFAULT_PALETTE);
    }
  }, [palette.length, setPalette]);

  // ── Transient editing UI ──────────────────────────────────────────
  const [marcVisible, setMarcVisible] = React.useState(authorMode);
  const [marcOpacity, setMarcOpacity] = React.useState(ONION_OPACITY);

  // Mutually-exclusive transform modes: marc and egg both want pointer
  // events on the canvas, so enabling one disables the other.
  const [marcTransformMode, _setMarcTransformMode] = React.useState(false);
  const [eggTransformMode, _setEggTransformMode] = React.useState(false);
  const setMarcTransformMode = React.useCallback((value) => {
    _setMarcTransformMode((cur) => {
      const next = typeof value === "function" ? value(cur) : value;
      if (next) _setEggTransformMode(false);
      return next;
    });
  }, []);
  const setEggTransformMode = React.useCallback((value) => {
    _setEggTransformMode((cur) => {
      const next = typeof value === "function" ? value(cur) : value;
      if (next) _setMarcTransformMode(false);
      return next;
    });
  }, []);

  // Author A/B toggle: temporarily flip lighting off to compare.
  const [lightingEnabled, setLightingEnabled] = React.useState(true);

  // Egg warmth: per-marc, persisted. Different marcs need different warmth
  // settings (varies with skin tone / lighting in the source photo), so the
  // author's tuned value rides along with the rest of the marc state into
  // localStorage and into the saved reference.
  const [eggWarmth, setEggWarmth] = useLocalStorageState(
    key("eggWarmth"),
    DEFAULT_EGG_WARMTH,
  );
  // Redshift mirrors warmth: per-marc, persisted, biases the tint toward red.
  const [eggRedshift, setEggRedshift] = useLocalStorageState(
    key("eggRedshift"),
    DEFAULT_EGG_REDSHIFT,
  );

  const setPaletteSlot = React.useCallback(
    (index, hex) => {
      setPalette((prev) => {
        const next = [...prev];
        next[index] = hex;
        return next;
      });
    },
    [setPalette],
  );

  // ── Hotkeys ───────────────────────────────────────────────────────
  useKey("m", () => {
    if (authorMode) setMarcVisible((v) => !v);
  });
  useKey("l", () => {
    if (authorMode) setLightingEnabled((v) => !v);
  });

  // ── Derived ───────────────────────────────────────────────────────
  // The marc reference photo doubles as the onion overlay in the drawing
  // pane during author mode. The easel/ReferencePhoto always shows it; the
  // onion is author-only, so we hand back null otherwise.
  const marcOnionSrc = authorMode ? marcImageUrl(urlState.marcId) : null;

  // "Save" no longer means "download a file." It builds the unlabeled JSON,
  // stashes it in sessionStorage under a handoff key, and navigates to
  // ?label={marcId}. LabelApp picks the handoff up on mount — so the
  // author never has to drop a file on disk before labeling. The labeled
  // reference.json is the only artifact that gets downloaded (from label
  // mode), and that's the only file that's actually committed to the repo.
  const save = React.useCallback(async () => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    // Default eggLights is one light with intensity=0, shadow=0 (no
    // relight applied). If the author never opened the lighting
    // controls, that's what gets saved — the egg renders flat in
    // play mode and the marc looks plastic. Cheap guard: warn before
    // committing a trace that has no light tuned at all. Author can
    // confirm-through if they really meant it.
    const isUnlit =
      !eggLights ||
      eggLights.length === 0 ||
      eggLights.every((l) => (l?.intensity ?? 0) === 0 && (l?.shadow ?? 0) === 0);
    if (isUnlit) {
      const ok = window.confirm(
        "No lighting configured for this marc — the egg will render flat. Save anyway?",
      );
      if (!ok) return;
    }

    const result = await saveReference({
      canvas,
      palette,
      marcId: urlState.marcId,
      eggId: DEFAULT_EGG_ID,
      marcSrc: marcImageUrl(urlState.marcId),
      eggSrc: eggImageUrl(DEFAULT_EGG_ID),
      marcTransform,
      eggTransform,
      eggLights,
      eggWarmth,
      eggRedshift,
    });
    writeUnlabeledHandoff(urlState.marcId, result.json);
    // eslint-disable-next-line no-console
    console.log(
      `Built reference-unlabeled (marc ${urlState.marcId}) — ${result.regionCount} regions, ${(result.bytes / 1024).toFixed(1)}KB. Handing off to label mode.`,
    );
    window.location.search = `?label=${urlState.marcId}`;
  }, [
    palette,
    marcTransform,
    eggTransform,
    eggLights,
    eggWarmth,
    eggRedshift,
    urlState.marcId,
    canvasRef,
  ]);

  return {
    // Persisted
    palette,
    setPalette,
    setPaletteSlot,
    marcTransform,
    setMarcTransform,
    eggTransform,
    setEggTransform,
    eggLights,
    setEggLights,
    // Transient
    marcVisible,
    setMarcVisible,
    marcOpacity,
    setMarcOpacity,
    marcTransformMode,
    setMarcTransformMode,
    eggTransformMode,
    setEggTransformMode,
    lightingEnabled,
    setLightingEnabled,
    eggWarmth,
    setEggWarmth,
    eggRedshift,
    setEggRedshift,
    // Resets
    resetMarcTransform: () => setMarcTransform(DEFAULT_MARC_TRANSFORM),
    resetEggTransform: () => setEggTransform(DEFAULT_EGG_TRANSFORM),
    resetEggLights: () => setEggLights(DEFAULT_EGG_LIGHTS),
    // Derived
    marcOnionSrc,
    save,
  };
}
